import axios, {AxiosRequestConfig} from 'axios';

import {AUTH_TOKEN, REFRESH_TOKEN, getFromLocalStorage, LOCALE} from 'utils/localStorage';
import {BANNED_USER_MESSAGE, EXPIRED_TOKEN_MESSAGE, INVALID_TOKEN_MESSAGE} from 'constants/errors';
import {logoutAction, setAuthInfoAction} from 'store/auth/auth.actions';
import {store} from 'store';

const {API_URL} = process.env;

interface callbackFunc {
  (...params: any): void;
}

interface extendedAxiosRequestConfig extends AxiosRequestConfig {
  disableAuth?: boolean,
}

let _isAlreadyRefreshing = false;
let subscribers: callbackFunc[] = [];

const instance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
    'Accept-Language': 'en',
  },
});

const refreshInstance = axios.create({
  baseURL: API_URL,
});

instance.interceptors.request.use(
  (config: extendedAxiosRequestConfig) => {
    if (!config?.headers) {
      config.headers = {};
    }
    if (!config?.headers?.Authorization) {
      const token = getFromLocalStorage(AUTH_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    if (config.disableAuth) {
      delete config.headers.Authorization;
    }
    const localeState = getFromLocalStorage(LOCALE);
    if (localeState?.locale) {
      config.headers['Accept-Language'] = localeState.locale;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const {response, config} = error;
    if (!response?.data) {
      return Promise.reject(error);
    }
    const {
      data: {code, message},
    } = response;
    if (code === 403 && message === BANNED_USER_MESSAGE) {
      store.dispatch(logoutAction());
      return Promise.reject(BANNED_USER_MESSAGE);
    }
    if (!config.headers.Authorization || code !== 401) {
      // return regular error
      return Promise.reject(error);
    }
    if (message === INVALID_TOKEN_MESSAGE) {
      store.dispatch(logoutAction());
      return Promise.reject(INVALID_TOKEN_MESSAGE);
    }

    if (message === EXPIRED_TOKEN_MESSAGE) {
      refreshToken();
      const {config: originalRequest = {}} = error;
      return new Promise((resolve, reject) => {
        addSubscriber((newAccessToken: string) => {
          if (!newAccessToken) {
            return reject({error: 'Failed to refresh token'});
          }
          originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
          resolve(instance.request(originalRequest));
        });
      });
    }
    return Promise.reject(error);
  }
);

const refreshToken = () => {
  if (!_isAlreadyRefreshing) {
    _isAlreadyRefreshing = true;
    const refreshToken = getFromLocalStorage(REFRESH_TOKEN);
    refreshInstance
      .post('/token/refresh', {refresh_token: refreshToken})
      .then((refreshResponse) => {
        const {data = {}} = refreshResponse;
        const {token, refresh_token: refreshToken} = data;
        _isAlreadyRefreshing = false;
        onAccessTokenFetched(token);
        store.dispatch(setAuthInfoAction({token, refreshToken}));
      })
      .catch(() => {
        _isAlreadyRefreshing = false;
        store.dispatch(logoutAction());
        onRefreshTokenError();
      });
  }
};

function onAccessTokenFetched(access_token: string) {
  subscribers = subscribers.filter((callback: callbackFunc) => callback(access_token));
}

function onRefreshTokenError() {
  subscribers = subscribers.filter((callback: callbackFunc) => callback(''));
}

function addSubscriber(callback: callbackFunc) {
  subscribers.push(callback);
}

export default instance;
